function hashStr(text) {
  let h = 2166136261;
  const s = String(text || '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const PROFESSION_OFFERS = {
  farmer: [
    { cost: [{ itemId: 'wheat', count: 20 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 12 },
    { cost: [{ itemId: 'bread', count: 6 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 10 },
    { cost: [{ itemId: 'emerald', count: 1 }], reward: { itemId: 'wheat_seed', count: 10 }, maxUses: 16 }
  ],
  fletcher: [
    { cost: [{ itemId: 'stick', count: 20 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 12 },
    { cost: [{ itemId: 'feather', count: 12 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 10 },
    { cost: [{ itemId: 'emerald', count: 1 }], reward: { itemId: 'arrow', count: 16 }, maxUses: 14 }
  ],
  blacksmith: [
    { cost: [{ itemId: 'iron_ingot', count: 8 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 12 },
    { cost: [{ itemId: 'emerald', count: 6 }], reward: { itemId: 'iron_pickaxe', count: 1 }, maxUses: 6 },
    { cost: [{ itemId: 'emerald', count: 4 }], reward: { itemId: 'iron_sword', count: 1 }, maxUses: 8 }
  ],
  armorer: [
    { cost: [{ itemId: 'iron_ingot', count: 10 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 10 },
    { cost: [{ itemId: 'emerald', count: 5 }], reward: { itemId: 'iron_helmet', count: 1 }, maxUses: 7 },
    { cost: [{ itemId: 'emerald', count: 9 }], reward: { itemId: 'iron_chestplate', count: 1 }, maxUses: 5 }
  ],
  cleric: [
    { cost: [{ itemId: 'rotten_flesh', count: 20 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 10 },
    { cost: [{ itemId: 'emerald', count: 4 }], reward: { itemId: 'ender_eye', count: 1 }, maxUses: 6 },
    { cost: [{ itemId: 'emerald', count: 2 }], reward: { itemId: '38', count: 2 }, maxUses: 8 }
  ],
  butcher: [
    { cost: [{ itemId: 'beef_raw', count: 10 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 10 },
    { cost: [{ itemId: 'pork_raw', count: 10 }], reward: { itemId: 'emerald', count: 1 }, maxUses: 10 },
    { cost: [{ itemId: 'emerald', count: 2 }], reward: { itemId: 'beef_cooked', count: 6 }, maxUses: 10 }
  ]
};

export function createVillagerTradeSystem({
  ui,
  itemDefs,
  t,
  onRequestClose = null
}) {
  const villagerTradeStates = new Map();
  let openVillager = null;
  let statusText = '';

  const overlayEl = document.getElementById('villager-trade-overlay');
  const titleEl = document.getElementById('villager-trade-title');
  const hintEl = document.getElementById('villager-trade-hint');
  const profileEl = document.getElementById('villager-trade-profile');
  const offersEl = document.getElementById('villager-trade-offers');
  const statusEl = document.getElementById('villager-trade-status');
  const closeBtnEl = document.getElementById('villager-trade-close-btn');

  function getItemName(itemId) {
    const key = `item.${itemId}`;
    const localized = t(key);
    if (localized !== key) return localized;
    return itemDefs[itemId]?.name || String(itemId);
  }

  function getProfessionKey(profession) {
    const key = `villager.profession.${profession || 'farmer'}`;
    const localized = t(key);
    return localized === key ? String(profession || 'farmer') : localized;
  }

  function makeOfferState(baseOffer, villagerId, index) {
    const salt = hashStr(`${villagerId}:${index}`);
    const out = {
      cost: [],
      reward: { itemId: baseOffer.reward.itemId, count: baseOffer.reward.count },
      maxUses: Math.max(1, Math.floor(baseOffer.maxUses || 8)),
      used: 0
    };

    for (const cost of baseOffer.cost || []) {
      const adjust = ((salt >> (index % 7)) & 3) - 1;
      out.cost.push({
        itemId: cost.itemId,
        count: Math.max(1, Math.floor(cost.count + adjust))
      });
    }
    return out;
  }

  function getOrCreateVillagerState(villager) {
    const key = String(villager?.id || '');
    if (!key) return null;

    if (!villagerTradeStates.has(key)) {
      const profession = String(villager?.profession || 'farmer');
      const templates = PROFESSION_OFFERS[profession] || PROFESSION_OFFERS.farmer;
      const offers = templates.map((offer, index) => makeOfferState(offer, key, index));
      villagerTradeStates.set(key, { profession, offers, lastRestockDay: 1 });
    }
    return villagerTradeStates.get(key);
  }

  function isOpen() {
    return !!openVillager;
  }

  function canAffordOffer(offer) {
    for (const cost of offer.cost) {
      if (!ui.hasItem(cost.itemId, cost.count)) return false;
    }
    return true;
  }

  function getRewardCapacity(itemId) {
    if (typeof ui.getInsertCapacity === 'function') return ui.getInsertCapacity(itemId);
    return Number.MAX_SAFE_INTEGER;
  }

  function render() {
    if (!isOpen() || !overlayEl || !offersEl) return;
    const state = getOrCreateVillagerState(openVillager);
    if (!state) return;

    if (titleEl) titleEl.innerText = t('villager.trade.title');
    if (hintEl) hintEl.innerText = t('villager.trade.hint');
    if (closeBtnEl) closeBtnEl.innerText = t('villager.trade.close');

    if (profileEl) {
      profileEl.innerText = t('villager.trade.profile', {
        name: openVillager.name || 'Villager',
        profession: getProfessionKey(state.profession)
      });
    }

    offersEl.innerHTML = '';
    state.offers.forEach((offer, index) => {
      const row = document.createElement('div');
      row.className = 'villager-offer-row';

      const soldOut = offer.used >= offer.maxUses;
      const canAfford = canAffordOffer(offer);
      const costsText = offer.cost.map((cost) => `${getItemName(cost.itemId)} x${cost.count}`).join(' + ');
      const rewardText = `${getItemName(offer.reward.itemId)} x${offer.reward.count}`;

      const costEl = document.createElement('div');
      costEl.className = 'villager-offer-cost';
      costEl.innerText = costsText;

      const arrowEl = document.createElement('div');
      arrowEl.className = 'villager-offer-arrow';
      arrowEl.innerText = '→';

      const rewardEl = document.createElement('div');
      rewardEl.className = 'villager-offer-reward';
      rewardEl.innerText = rewardText;

      const metaEl = document.createElement('div');
      metaEl.className = 'villager-offer-meta';
      metaEl.innerText = t('villager.trade.uses', {
        current: offer.used,
        max: offer.maxUses
      });

      const actionEl = document.createElement('button');
      actionEl.className = 'villager-offer-btn';
      actionEl.innerText = soldOut ? t('villager.trade.sold_out') : t('villager.trade.buy');
      actionEl.disabled = soldOut;
      if (!soldOut && !canAfford) actionEl.classList.add('insufficient');
      actionEl.addEventListener('click', () => {
        performTrade(index);
      });

      row.appendChild(costEl);
      row.appendChild(arrowEl);
      row.appendChild(rewardEl);
      row.appendChild(metaEl);
      row.appendChild(actionEl);
      offersEl.appendChild(row);
    });

    if (statusEl) {
      statusEl.innerText = statusText || '';
      statusEl.style.opacity = statusText ? '1' : '0.65';
    }
  }

  function performTrade(index) {
    if (!isOpen()) return false;
    const state = getOrCreateVillagerState(openVillager);
    if (!state) return false;
    const offer = state.offers[index];
    if (!offer) return false;

    if (offer.used >= offer.maxUses) {
      statusText = t('villager.trade.sold_out');
      render();
      return false;
    }
    if (!canAffordOffer(offer)) {
      statusText = t('villager.trade.cannot_afford');
      render();
      return false;
    }

    const capacity = getRewardCapacity(offer.reward.itemId);
    if (capacity < offer.reward.count) {
      statusText = t('villager.trade.inventory_full');
      render();
      return false;
    }

    for (const cost of offer.cost) {
      if (!ui.removeItem(cost.itemId, cost.count)) {
        statusText = t('villager.trade.cannot_afford');
        render();
        return false;
      }
    }

    if (!ui.addItem(offer.reward.itemId, offer.reward.count)) {
      statusText = t('villager.trade.inventory_full');
      render();
      return false;
    }

    offer.used += 1;
    ui.assignItemToHotbar(offer.reward.itemId);
    statusText = t('villager.trade.success', {
      item: getItemName(offer.reward.itemId),
      count: offer.reward.count
    });
    render();
    return true;
  }

  function openForVillager(villager) {
    if (!villager || !villager.id) return false;
    openVillager = {
      id: String(villager.id),
      profession: String(villager.profession || 'farmer'),
      name: villager.name || t('villager.default_name')
    };
    statusText = '';

    if (overlayEl) overlayEl.style.display = 'flex';
    document.body.classList.add('inventory-open');
    render();
    return true;
  }

  function close() {
    openVillager = null;
    statusText = '';
    if (overlayEl) overlayEl.style.display = 'none';
    document.body.classList.remove('inventory-open');
  }

  function refreshLocale() {
    render();
  }

  function restockForDay(dayCount = 1) {
    const day = Math.max(1, Math.floor(dayCount || 1));
    let didRestock = false;

    for (const state of villagerTradeStates.values()) {
      if (!state) continue;
      if (day <= (state.lastRestockDay || 1)) continue;
      state.lastRestockDay = day;
      for (const offer of state.offers || []) {
        offer.used = 0;
      }
      didRestock = true;
    }

    if (didRestock && isOpen()) {
      statusText = t('villager.trade.restocked');
      render();
    }
  }

  function normalizeOffer(offer) {
    if (!offer || typeof offer !== 'object') return null;
    const rewardId = String(offer?.reward?.itemId || '');
    const rewardCount = Math.floor(Number(offer?.reward?.count));
    if (!itemDefs[rewardId] || !Number.isFinite(rewardCount) || rewardCount <= 0) return null;

    const costsRaw = Array.isArray(offer.cost) ? offer.cost : [];
    const cost = [];
    for (const c of costsRaw) {
      const itemId = String(c?.itemId || '');
      const count = Math.floor(Number(c?.count));
      if (!itemDefs[itemId] || !Number.isFinite(count) || count <= 0) continue;
      cost.push({ itemId, count });
    }
    if (cost.length <= 0) return null;

    const maxUses = Math.max(1, Math.floor(Number(offer.maxUses) || 1));
    const used = Math.max(0, Math.min(maxUses, Math.floor(Number(offer.used) || 0)));

    return {
      cost,
      reward: { itemId: rewardId, count: rewardCount },
      maxUses,
      used
    };
  }

  function exportState() {
    const villagers = [];
    for (const [id, state] of villagerTradeStates.entries()) {
      villagers.push({
        id,
        profession: state.profession,
        lastRestockDay: Number(state.lastRestockDay) || 1,
        offers: Array.isArray(state.offers)
          ? state.offers.map((offer) => ({
              cost: Array.isArray(offer.cost)
                ? offer.cost.map((c) => ({ itemId: c.itemId, count: c.count }))
                : [],
              reward: {
                itemId: offer.reward?.itemId,
                count: offer.reward?.count
              },
              maxUses: offer.maxUses,
              used: offer.used
            }))
          : []
      });
    }
    return { villagers };
  }

  function importState(snapshot = null) {
    villagerTradeStates.clear();
    const villagers = Array.isArray(snapshot?.villagers) ? snapshot.villagers : [];
    for (const entry of villagers) {
      const id = String(entry?.id || '');
      if (!id) continue;
      const profession = String(entry?.profession || 'farmer');
      const lastRestockDay = Math.max(1, Math.floor(Number(entry?.lastRestockDay) || 1));
      const offers = [];
      const rawOffers = Array.isArray(entry?.offers) ? entry.offers : [];
      for (const raw of rawOffers) {
        const normalized = normalizeOffer(raw);
        if (!normalized) continue;
        offers.push(normalized);
      }
      if (offers.length <= 0) continue;
      villagerTradeStates.set(id, {
        profession,
        offers,
        lastRestockDay
      });
    }

    if (isOpen()) render();
  }

  if (closeBtnEl) {
    closeBtnEl.addEventListener('click', () => {
      if (typeof onRequestClose === 'function') onRequestClose();
      else close();
    });
  }

  return {
    openForVillager,
    close,
    isOpen,
    refreshLocale,
    exportState,
    importState,
    update(dayCount = 1) {
      restockForDay(dayCount);
      if (isOpen()) render();
    }
  };
}
